import { close } from 'ionicons/icons';
import { Injectable } from '@angular/core';
import {
  CreatePaymentSheetOption,
  Stripe,
  ApplePayEventsEnum,
} from '@capacitor-community/stripe';
import { environment } from '../../environments/environment';
import { Capacitor } from '@capacitor/core';
import { loadStripe } from '@stripe/stripe-js';
import { HttpClient } from '@angular/common/http';
import { TranslateConfigService } from './translate-config.service';
import {
  ActionSheetController,
  ToastController,
  LoadingController,
} from '@ionic/angular';
import { PaymentService } from './api/payment.service';
import { StripeConnectStateService } from './stripe-connect-state.service';
import { AppStorageService } from './app-storage.service';
import { StorageKeys } from './storage-keys';

type StripePaymentFlowResult<T> = {
  success: boolean;
  payload?: T;
  error?: any;
  canceled?: boolean;
};

export interface PaymentOutcome {
  success: boolean;
  error?: unknown;
  canceled?: boolean;
}

export interface PaymentOutcomeVerificationConfig<TVerification> {
  initialResult: PaymentOutcome;
  verify: () => Promise<TVerification>;
  isVerified: (verification: TVerification) => boolean;
  attempts?: number;
  retryDelayMs?: number;
}

export interface VerifiedPaymentOutcome<TVerification> extends PaymentOutcome {
  verification?: TVerification;
}

interface NativePaymentPreparation {
  reference: string;
  options: {
    paymentIntentClientSecret: string;
    customerId?: string;
    customerEphemeralKeySecret?: string;
    merchantDisplayName?: string;
    amount?: number;
    currency?: string;
  };
}

interface WebPaymentPreparation {
  reference: string;
  session?: any;
  clientSecret: string;
}

interface StripePaymentFlowConfig<T> {
  prepareNative: () => Promise<NativePaymentPreparation>;
  prepareWeb: () => Promise<WebPaymentPreparation>;
  confirmPayment?: (params: {
    reference: string;
    isWebCheckout: boolean;
  }) => Promise<T>;
  successPredicate?: (payload: T) => boolean;
  onSuccess?: (payload: T) => void | Promise<void>;
  loadingKey?: string;
  processingKey?: string;
  errorKey?: string;
}
export function isPaymentCancellationError(error: any): boolean {
  if (!error) return false;
  const raw = typeof error === 'string' ? error : String(error?.message ?? error);
  const normalized = raw.trim().toLowerCase().replace(/[_-]/g, ' ');
  return (
    normalized === 'payment canceled' ||
    normalized === 'payment cancelled' ||
    normalized === 'web checkout closed'
  );
}

@Injectable({
  providedIn: 'root',
})
export class StripeService {
  private initialized = false;
  private runtimePublishableKey: string | null = null;
  private checkoutForm: any;
  private onExitCallback: (() => void) | undefined;
  private createdMountElement: HTMLElement | undefined;
  // Handlers for overlay interactions so we can remove them on close
  private overlayClickHandler: ((e: Event) => void) | undefined;
  private overlayKeyHandler: ((e: KeyboardEvent) => void) | undefined;
  // Global guard to prevent spawning multiple payment flows simultaneously
  private paymentInProgress: boolean = false;

  constructor(
    private translateService: TranslateConfigService,
    private actionSheetController: ActionSheetController,
    private paymentService: PaymentService,
    private toastController: ToastController,
    private loadingController: LoadingController
    , private http: HttpClient,
    private stripeConnectState: StripeConnectStateService,
    private storage: AppStorageService
  ) {}

  private isNativePlatform(): boolean {
    const platform = Capacitor.getPlatform();
    return platform === 'ios' || platform === 'android';
  }

  private async executeStripePaymentFlow<T>(
    config: StripePaymentFlowConfig<T>
  ): Promise<StripePaymentFlowResult<T>> {
    if (this.paymentInProgress) {
      return { success: false, error: new Error('PAYMENT_IN_PROGRESS') };
    }

    this.paymentInProgress = true;

    const loadingKey = config.loadingKey ?? 'COMMON.LOADING';
    const processingKey = config.processingKey ?? 'PROCESSING_PAYMENT';
    const errorKey = config.errorKey ?? 'DETAILS.PAYMENT_ERROR';

    let loading: HTMLIonLoadingElement | null = null;
    let reference: string | undefined;
    const isNative = this.isNativePlatform();

    try {
      const loadingMessage =
        this.translateService.getItemTranslated(loadingKey) ?? 'Loading...';

      loading = await this.loadingController.create({
        message: loadingMessage,
        spinner: 'crescent',
      });
      await loading.present();

      if (isNative) {
        const nativeData = await config.prepareNative();
        if (!nativeData?.options?.paymentIntentClientSecret) {
          throw new Error('PAYMENT_INTENT_ERROR');
        }
        reference = nativeData.reference;
        try {
          await loading.dismiss();
        } catch {}
        loading = null;
        await this.presentMobilePaymentSheet(nativeData.options);
      } else {
        const webData = await config.prepareWeb();
        if (!webData?.clientSecret) {
          throw new Error('CHECKOUT_SESSION_ERROR');
        }
        reference = webData.reference;
        try {
          await loading.dismiss();
        } catch {}
        loading = null;
        await new Promise<void>((resolve, reject) => {
          this.openSession(
            { ...(webData.session ?? {}), clientSecret: webData.clientSecret },
            () => resolve(),
            () => reject(new Error('WEB_CHECKOUT_CLOSED'))
          );
        });
      }

      if (!reference) {
        throw new Error('PAYMENT_CANCELED');
      }

      let payload: T;

      if (config.confirmPayment) {
        const processingMessage =
          this.translateService.getItemTranslated(processingKey) ??
          this.translateService.getItemTranslated('PROCESSING_PAYMENT') ??
          'Procesando...';

        loading = await this.loadingController.create({
          message: processingMessage,
          spinner: 'crescent',
        });
        await loading.present();

        payload = await config.confirmPayment({
          reference,
          isWebCheckout: !isNative,
        });

        const success =
          config.successPredicate?.(payload) ??
          ((payload as any)?.success === true);

        if (!success) {
          throw new Error('SAVE_PAYMENT_ERROR');
        }
      } else {
        // Sin confirmPayment: el webhook de Stripe procesará el pago
        payload = { success: true } as any;
      }

      if (config.onSuccess) {
        await config.onSuccess(payload);
      }

      try {
        await loading?.dismiss();
      } catch {}
      loading = null;

      return { success: true, payload };
    } catch (err: any) {
      try {
        await loading?.dismiss();
      } catch {}

      const code = String(err?.message || '');
      const canceled =
        code === 'PAYMENT_CANCELED' ||
        code === 'Payment canceled' ||
        code === 'WEB_CHECKOUT_CLOSED';

      if (!canceled) {
        try {
          const key = err?.message || errorKey;
          const message = this.translateService.getItemTranslated(
            key.includes('.') ? key : errorKey
          );
          const toast = await this.toastController.create({
            message,
            duration: 3000,
            color: 'danger',
          });
          toast.present();
        } catch {}
      }

      return { success: false, error: canceled ? undefined : err, canceled };
    } finally {
      try {
        await loading?.dismiss();
      } catch {}
      this.paymentInProgress = false;
    }
  }

  /**
   * Public wrapper to execute a custom payment flow using the unified Stripe flow.
   * Messages and other pages can pass the same config shape as internal flows.
   */
  public async executeCustomFlow<T>(config: any): Promise<any> {
    return this.executeStripePaymentFlow<T>(config as StripePaymentFlowConfig<T>);
  }

  public async verifyPaymentOutcome<TVerification>(
    config: PaymentOutcomeVerificationConfig<TVerification>
  ): Promise<VerifiedPaymentOutcome<TVerification>> {
    const {
      initialResult,
      verify,
      isVerified,
      attempts = 3,
      retryDelayMs = 750,
    } = config;

    if (!initialResult.success) {
      return {
        success: false,
        canceled: !!initialResult.canceled,
        error: initialResult.error,
      };
    }

    let lastError: unknown = new Error('PAYMENT_NOT_CONFIRMED');

    for (let attempt = 0; attempt < attempts; attempt += 1) {
      try {
        const verification = await verify();
        if (isVerified(verification)) {
          return {
            success: true,
            verification,
          };
        }
      } catch (error) {
        lastError = error;
      }

      if (attempt < attempts - 1) {
        await this.wait(retryDelayMs);
      }
    }

    return {
      success: false,
      error: lastError,
      canceled: false,
    };
  }

  private async wait(milliseconds: number): Promise<void> {
    await new Promise<void>((resolve) => setTimeout(resolve, milliseconds));
  }

  // Present the same native choice UI used in DetailsPage so service-managed flows match component flows
  private async presentNativeChoiceActionSheet(
    paymentIntent: string,
    sheetOptions: CreatePaymentSheetOption,
    amount: number = 0
  ): Promise<any> {
    // Return a promise that resolves when the chosen native payment completes,
    // or rejects when the user cancels / an error occurs.
    return new Promise(async (resolve, reject) => {
      let settled = false;
      const platform = Capacitor.getPlatform();
      const header = await this.translateService
        .getItemTranslatedAsync('DETAILS.PAY_WITH')
        .toPromise()
        .catch(() => 'Pagar con:');
      const stripeLabel = await this.translateService
        .getItemTranslatedAsync('DETAILS.PAY_STRIPE')
        .toPromise()
        .catch(() => 'Pay with card');
      const appleLabel = 'Apple Pay';
      const googleLabel = 'Google Pay';

      const buttons: any[] = [];

      if (platform === 'ios') {
        buttons.push({
          text: appleLabel,
          icon: 'logo-apple',
          handler: async () => {
            try {
              settled = true; // user chose Apple Pay
              const currencyCode = (sheetOptions as any).currency ?? 'EUR';
              const countryCode = sheetOptions.countryCode ?? 'ES';
              const totalAmount = Number.isFinite(amount)
                ? Number(Math.round((amount as number) * 100) / 100).toFixed(2)
                : '0.00';
              await Stripe.createApplePay({
                merchantIdentifier:
                  sheetOptions.applePayMerchantId ?? 'merchant.com.foundek.app',
                countryCode,
                currency: currencyCode,
                paymentIntentClientSecret: paymentIntent,
                paymentRequest: {
                  countryCode,
                  currencyCode,
                  total: {
                    label: sheetOptions.merchantDisplayName ?? 'Foundek',
                    amount: String(totalAmount),
                  },
                },
                paymentSummaryItems: [
                  {
                    label: sheetOptions.merchantDisplayName ?? 'Foundek',
                    amount: Number(totalAmount),
                  },
                ],
              } as any);
              const result = await Stripe.presentApplePay();
              try {
                await action.dismiss();
              } catch {}
              const paymentResult = (result as any)?.paymentResult;
              if (
                paymentResult &&
                (paymentResult === (ApplePayEventsEnum as any)?.Completed ||
                  String(paymentResult).toLowerCase().includes('complet'))
              ) {
                try {
                  await (Stripe as any).confirmApplePayPayment?.({
                    clientSecret: paymentIntent,
                  });
                } catch {}
                return resolve(result);
              }
              if (
                paymentResult &&
                String(paymentResult).toLowerCase().includes('cancel')
              )
                return reject(new Error('Payment canceled'));
              return reject(new Error('Apple Pay failed'));
            } catch (e) {
              //try { await Stripe.createPaymentSheet(sheetOptions); const ps = await Stripe.presentPaymentSheet(); if ((ps as any)?.paymentResult === 'canceled') return reject(new Error('Payment canceled')); return resolve(ps); } catch (err) { return reject(err); }
            }
          },
        });
      } else if (platform === 'android') {
        buttons.push({
          text: googleLabel,
          icon: 'logo-google',
          handler: async () => {
            try {
              settled = true; // user chose Google Pay
              await Stripe.createGooglePay({
                paymentIntentClientSecret: paymentIntent,
                currency: (sheetOptions as any).currency ?? 'EUR',
                isTesting: !environment.production,
                merchantName: sheetOptions.merchantDisplayName ?? 'Foundek',
                countryCode: sheetOptions.countryCode ?? 'ES',
              } as any);
              const gpRes = await Stripe.presentGooglePay();
              try { await action.dismiss(); } catch {}
              if ((gpRes as any)?.paymentResult && String((gpRes as any).paymentResult).toLowerCase().includes('cancel')) {
                return reject(new Error('Payment canceled'));
              }
              return resolve(gpRes);
            } catch (e) {
              try {
                await Stripe.createPaymentSheet(sheetOptions);
                const ps = await Stripe.presentPaymentSheet();
                try { await action.dismiss(); } catch {}
                if ((ps as any)?.paymentResult === 'canceled') return reject(new Error('Payment canceled'));
                return resolve(ps);
              } catch (fallbackErr) {
                try { await action.dismiss(); } catch {}
                return reject(fallbackErr || e);
              }
            }
          },
        });
      }

      // Card option always
      buttons.push({
        text: stripeLabel,
        icon: 'card',
        handler: async () => {
          try {
            settled = true; // user chose Card
            await Stripe.createPaymentSheet(sheetOptions);
            const psRes = await Stripe.presentPaymentSheet();
            try {
              await action.dismiss();
            } catch {}
            if ((psRes as any)?.paymentResult === 'canceled')
              return reject(new Error('Payment canceled'));
            return resolve(psRes);
          } catch (e) {
            try {
              await action.dismiss();
            } catch {}
            return reject(e || new Error('PaymentSheet error'));
          }
        },
      });

      // Add explicit cancel button at end
      buttons.push({
        text: await this.translateService
          .getItemTranslatedAsync('COMMON.CANCEL')
          .toPromise()
          .catch(() => 'Cancelar'),
        role: 'cancel',
        icon: 'close',
        handler: () => {
          /* just dismiss */
        },
      });
      const action = await this.actionSheetController.create({
        header,
        buttons,
      });
      await action.present();

      // If the sheet is dismissed without selecting an option (e.g. backdrop / cancel), reject to unblock flow
      action.onDidDismiss().then((detail) => {
        if (!settled) {
          settled = true;
          reject(new Error('PAYMENT_CANCELED'));
        }
      });
    });
  }

  /**
   * Inicializa Stripe solo cuando se necesita
   */
  public initializeStripe(): Promise<void> {
    return this.fetchPublishableKey().then((key) => {
      if (!this.initialized) {
        Stripe.initialize({
          publishableKey: key,
        });
        this.initialized = true;
      }
    });
  }

  private async fetchPublishableKey(): Promise<string> {
    // Return cached runtime key if present
    if (this.runtimePublishableKey) return this.runtimePublishableKey;

    // Try cached storage first
    try {
      const cached = this.storage.get(StorageKeys.STRIPE_PUBLISHABLE_KEY);
      if (cached) {
        this.runtimePublishableKey = cached;
        return cached;
      }
    } catch (e) {}

    // Fetch from backend public endpoint (moved to app route)
    try {
      // Request the publishable key from the configured API server (avoid relative localhost)
      const resp: any = await this.http
        .get(`${environment.apiUrl.replace(/\/$/, '')}/stripe/publishable-key`)
        .toPromise();
      const key =
        resp?.data?.publishable_key || resp?.publishable_key || resp?.data?.key;
      if (key) {
        this.runtimePublishableKey = key;
        try {
          this.storage.set(StorageKeys.STRIPE_PUBLISHABLE_KEY, key);
        } catch (e) {}
        return key;
      }
    } catch (e) {
    }

    // Fallback to environment key
    this.runtimePublishableKey = environment.stripe_key;
    return this.runtimePublishableKey;
  }

  /**
   * Unified flow to feature an article (pay to highlight for N days)
   * Handles native vs web, user method selection, and backend confirmation.
   */
  public async featureArticle(params: {
    articleId: number;
    days: number;
    amount?: number;
    currency?: string;
    featuredService: any;
  }): Promise<{ success: boolean; error?: any; canceled?: boolean }> {
    const {
      articleId,
      days,
      amount = 0,
      currency = 'EUR',
      featuredService,
    } = params;

    const flow = await this.executeStripePaymentFlow<any>({
      prepareNative: async () => {
        const intentResp: any = await featuredService
          .featuredIntent(articleId, days)
          .toPromise();

        if (!intentResp?.paymentIntent) {
          throw new Error('PAYMENT_INTENT_ERROR');
        }

        const customerId =
          intentResp?.customer?.id ?? intentResp?.customer ?? undefined;
        const reference = intentResp?.tokenRef ?? intentResp?.paymentIntent;

        if (!reference) {
          throw new Error('PAYMENT_INTENT_ERROR');
        }

        const options: NativePaymentPreparation['options'] = {
          paymentIntentClientSecret: intentResp.paymentIntent,
          merchantDisplayName: 'Foundek',
          amount,
          currency,
        };

        if (customerId) {
          options.customerId = customerId;
        }
        if (intentResp?.ephemeralKey) {
          options.customerEphemeralKeySecret = intentResp.ephemeralKey;
        }

        return {
          reference,
          options,
        };
      },
      prepareWeb: async () => {
        const sessResp: any = await featuredService
          .featuredCheckoutSession(articleId, days)
          .toPromise();

        const sessionObj = sessResp?.session ?? sessResp;
        const clientSecret =
          sessionObj?.clientSecret ??
          sessionObj?.client_secret ??
          sessionObj?.paymentIntent;
        const sessionId = sessionObj?.id ?? sessionObj?.session_id;

        if (!sessionId || !clientSecret) {
          throw new Error('PAYMENT_INTENT_ERROR');
        }

        return {
          reference: sessionId,
          session: sessionObj,
          clientSecret,
        };
      },
      onSuccess: () => {},
    });

    if (flow.success) {
      return { success: true };
    }
    if (flow.canceled) {
      return { success: false, canceled: true };
    }
    if (flow.error) {
    }
    return { success: false, error: flow.error };
  }

  /** Expose read-only status so components can disable payment buttons */
  public isPaymentBusy(): boolean { return this.paymentInProgress; }

  // (removed) showConfirmDialog: unused DOM-based confirm helper

  /**
   * Present a mobile PaymentSheet using Capacitor Stripe with the given secrets.
   */
  public async presentMobilePaymentSheet(options: {
    customerId?: string;
    customerEphemeralKeySecret?: string;
    paymentIntentClientSecret: string;
    merchantDisplayName?: string;
    amount?: number; // optional amount in major units (e.g. 4.99)
    currency?: string; // optional currency code (default EUR)
    disableGooglePayAuto?: boolean; // if true, don't auto-attempt Google Pay even on Android
  }): Promise<void> {
    await this.initializeStripe();
    // Build payment sheet options and enable platform wallets where applicable
    const platform = Capacitor.getPlatform();
    const enableApplePay = platform === 'ios';
    const enableGooglePay = platform === 'android' && options.disableGooglePayAuto !== true;

    const sheetOptions: CreatePaymentSheetOption = {
      paymentIntentClientSecret: options.paymentIntentClientSecret,
      customerId: options.customerId,
      customerEphemeralKeySecret: options.customerEphemeralKeySecret,
      merchantDisplayName: options.merchantDisplayName ?? 'Foundek',
    };

    // Hint the native plugin to surface wallet buttons when available
    if (enableApplePay) {
      sheetOptions.enableApplePay = true;

      sheetOptions.applePayMerchantId = 'merchant.com.foundek.app'; // use app merchant ID
    }
    // Siempre mostrar hoja de elección nativa para consistencia (Apple Pay / Google Pay / Tarjeta)
    try {
      const amount = typeof options.amount === 'number' ? options.amount : 0;
      sheetOptions.countryCode = sheetOptions.countryCode ?? 'ES';
      (sheetOptions as any).currency = options.currency ?? 'EUR';
      if (enableApplePay) {
        sheetOptions.enableApplePay = true;
        sheetOptions.applePayMerchantId = 'merchant.com.foundek.app';
      }
      if (enableGooglePay) {
        sheetOptions.enableGooglePay = true;
        (sheetOptions as any).GooglePayIsTesting = !environment.production;
      }
      await this.presentNativeChoiceActionSheet(
        options.paymentIntentClientSecret,
        sheetOptions,
        amount
      );
      return;
    } catch (choiceErr) {
      if (isPaymentCancellationError(choiceErr)) {
        throw choiceErr;
      }
      try {
        await Stripe.createPaymentSheet(sheetOptions);
        const result = await Stripe.presentPaymentSheet();
        if ((result as any)?.paymentResult === 'canceled') {
          throw new Error('PAYMENT_CANCELED');
        }
        return;
      } catch (psErr) {
        throw psErr;
      }
    }
  }

  closeSession() {
    try {
      this.checkoutForm?.unmount();
    } catch (e) {
      // ignore
    }
    try {
      this.checkoutForm?.destroy();
    } catch (e) {
      // ignore
    }
    // try to unmount legacy card element if present
    try {
      (async () => {
        try {
          const key = await this.fetchPublishableKey();
          const stripe = await loadStripe(key as any);
          try {
            stripe?.elements()?.getElement('card')?.unmount();
          } catch (e) {}
        } catch (e) {}
      })();
    } catch (e) {}

    // call exit callback if provided
    try {
      if (this.onExitCallback) this.onExitCallback();
    } catch (e) {}
    this.onExitCallback = undefined;
    this.checkoutForm = undefined;
    // If we created a temporary mount element for embedded checkout, remove event handlers and DOM node
    try {
      const overlay = this.createdMountElement;
      if (overlay && this.overlayClickHandler) {
        try {
          (overlay as HTMLElement).removeEventListener(
            'click',
            this.overlayClickHandler as EventListener
          );
        } catch (e) {}
      }
      if (this.overlayKeyHandler) {
        try {
          document.removeEventListener(
            'keydown',
            this.overlayKeyHandler as EventListener
          );
        } catch (e) {}
      }
      if (overlay && overlay.parentNode) {
        try {
          overlay.parentNode.removeChild(overlay);
        } catch (e) {}
      }
    } catch (e) {
      // ignore removal errors
    }
    this.createdMountElement = undefined;
    this.overlayClickHandler = undefined;
    this.overlayKeyHandler = undefined;
  }
  public async openSession(
    session: any | string,
    onSuccess: () => any | Promise<any> = () => {},
    onExit: () => void = () => {}
  ) {
  const publishableKey = await this.fetchPublishableKey();
  let webStripe = await loadStripe(publishableKey as any);

    // Accept either a raw client secret string or an object with clientSecret/client_secret
    const clientSecret =
      typeof session === 'string'
        ? session
        : session?.clientSecret ??
          session?.client_secret ??
          session?.paymentIntent ??
          session?.client_secret_raw;

    if (!clientSecret) {
      try {
        onExit();
      } catch (e) {}
      return;
    }

    this.onExitCallback = onExit;

    try {
      // If a previous embedded checkout was left open, clean it up first to avoid
      // the `You cannot have multiple Embedded Checkout objects` error from Stripe.
      try {
        if (this.checkoutForm) {
          try {
            this.checkoutForm.unmount();
          } catch (e) {}
          try {
            this.checkoutForm.destroy();
          } catch (e) {}
          this.checkoutForm = undefined;
        }
      } catch (e) {}

      const checkoutForm = await webStripe?.initEmbeddedCheckout({
        clientSecret,
        onComplete: async () => {
          try {
          } catch (e) {}
          // Allow callers to return a Promise (they usually confirm backend). Await it so
          // we can clean up the mounted checkout UI after confirmation completes.
          try {
            const res = onSuccess();
            if (res && typeof (res as any).then === 'function') {
              await res;
            }
          } catch (e) {
            // If caller handler throws, call exit so they can cleanup and propagate
            try {
              onExit();
            } catch (err) {}
            return;
          }

          // Clean up mounted checkout UI here without invoking onExitCallback (this is a success path)
          try {
            checkoutForm?.unmount();
          } catch (e) {}
          try {
            checkoutForm?.destroy();
          } catch (e) {}
          // Remove any created overlay/mount element
          try {
            if (
              this.createdMountElement &&
              this.createdMountElement.parentNode
            ) {
              this.createdMountElement.parentNode.removeChild(
                this.createdMountElement
              );
            }
          } catch (e) {}
          this.createdMountElement = undefined;
          // Clear stored instance
          try {
            this.checkoutForm = undefined;
          } catch (e) {}
        },
      });

      if (!checkoutForm) {
        // ensure we call exit so caller can cleanup
        try {
          onExit();
        } catch (e) {}
        return;
      }

      // Ensure there is a mount target. If the page doesn't include an element with id
      // 'card-element', or if that element already contains children (which Stripe rejects),
      // create a minimal overlay container so embedded checkout can mount.
      let mountTarget: Element | null = document.querySelector('#card-element');
      if (!mountTarget) {
        try {
          const overlay = document.createElement('div');
          overlay.id = 'card-element-overlay';
          // Basic styles so it appears as a centered dialog; projects may override via CSS
          overlay.style.position = 'fixed';
          overlay.style.top = '0';
          overlay.style.left = '0';
          overlay.style.right = '0';
          overlay.style.bottom = '0';
          overlay.style.width = '100%';
          // Keep the overlay fixed and centered; the wrapper will handle internal scrolling.
          overlay.style.display = 'flex';
          // Anchor the dialog near the top on mobile so users can scroll to the bottom
          overlay.style.alignItems = 'flex-start';
          overlay.style.justifyContent = 'center';
          overlay.style.overflow = 'hidden';
          // reduce top/bottom padding so wrapper can use more vertical space
          overlay.style.padding = '12px 16px';
          overlay.style.boxSizing = 'border-box';
          try {
            // enable smooth scrolling on iOS
            overlay.style.setProperty('-webkit-overflow-scrolling', 'touch');
          } catch (e) {}
          overlay.style.zIndex = '99999';
          overlay.style.background = 'rgba(0,0,0,0.45)';

          // Create a visual wrapper (the dialog) and an inner empty container for Stripe
          const wrapper = document.createElement('div');
          wrapper.style.width = '420px';
          wrapper.style.maxWidth = '95%';
          wrapper.style.background = '#fff';
          wrapper.style.padding = '16px';
          wrapper.style.borderRadius = '8px';
          wrapper.style.boxShadow = '0 8px 24px rgba(0,0,0,0.2)';
          wrapper.style.position = 'relative';
          wrapper.style.maxHeight = 'calc(100vh - 24px)';
          wrapper.style.overflowY = 'auto';
          wrapper.style.margin = '12px';

          try {
            wrapper.style.setProperty('-webkit-overflow-scrolling', 'touch');
          } catch (e) {}

          const container = document.createElement('div');
          container.id = 'card-element';
          // Keep the container empty for Stripe to mount into. It should not have
          // decoration or child nodes before mount.
          container.style.width = '100%';
          container.style.minHeight = '64px';
          container.style.boxSizing = 'border-box';
          container.style.overflowY = 'auto';

          const closeBtn = document.createElement('button');
          closeBtn.setAttribute('aria-label', 'Close checkout');
          closeBtn.innerText = '\u00d7';
          closeBtn.style.position = 'absolute';
          closeBtn.style.top = '8px';
          closeBtn.style.right = '8px';
          closeBtn.style.background = 'transparent';
          closeBtn.style.border = 'none';
          closeBtn.style.fontSize = '24px';
          closeBtn.style.cursor = 'pointer';
          closeBtn.style.lineHeight = '1';
          closeBtn.style.padding = '4px';
          closeBtn.style.zIndex = '100001';

          wrapper.appendChild(container);
          wrapper.appendChild(closeBtn);
          overlay.appendChild(wrapper);
          document.body.appendChild(overlay);
          // keep reference so we can remove it on close
          this.createdMountElement = overlay;

          const closeHandler = () => {
            try {
              if (this.onExitCallback) this.onExitCallback();
            } catch (e) {}
            try {
              if (
                this.createdMountElement &&
                this.createdMountElement.parentNode
              ) {
                this.createdMountElement.parentNode.removeChild(
                  this.createdMountElement
                );
              }
            } catch (e) {}
            this.createdMountElement = undefined;
          };
          closeBtn.addEventListener('click', closeHandler);

          // Click on overlay background should also close the overlay if clicked outside wrapper
          const overlayClickHandler = (evt: Event) => {
            if (evt.target === overlay) {
              closeHandler();
            }
          };
          overlay.addEventListener('click', overlayClickHandler);
          this.overlayClickHandler = overlayClickHandler;

          // ESC key should close the overlay
          const keyHandler = (evt: KeyboardEvent) => {
            if (evt.key === 'Escape' || evt.key === 'Esc') {
              closeHandler();
            }
          };
          document.addEventListener('keydown', keyHandler);
          this.overlayKeyHandler = keyHandler;

          mountTarget = container;
        } catch (e) {
        }
      } else if (
        mountTarget &&
        mountTarget.childNodes &&
        mountTarget.childNodes.length > 0
      ) {
        // If the existing element has children, Stripe's embedded checkout will reject mounting.
        // Create an isolated overlay + container and mount there instead.
        try {
          const overlay = document.createElement('div');
          overlay.id = 'card-element-overlay';
          overlay.style.position = 'fixed';
          overlay.style.top = '0';
          overlay.style.left = '0';
          overlay.style.right = '0';
          overlay.style.bottom = '0';
          overlay.style.width = '100%';
          // Keep the overlay fixed and centered; the wrapper will handle internal scrolling.
          overlay.style.display = 'flex';
          // Anchor dialog near top to allow full internal scrolling on mobile
          overlay.style.alignItems = 'flex-start';
          overlay.style.justifyContent = 'center';
          overlay.style.overflow = 'hidden';
          overlay.style.padding = '12px 16px';
          overlay.style.boxSizing = 'border-box';
          try {
            overlay.style.setProperty('-webkit-overflow-scrolling', 'touch');
          } catch (e) {}
          overlay.style.zIndex = '99999';
          overlay.style.background = 'rgba(0,0,0,0.45)';

          // Create wrapper/dialog and an inner empty container for Stripe to mount into
          const wrapper = document.createElement('div');
          wrapper.style.width = '420px';
          wrapper.style.maxWidth = '95%';
          wrapper.style.background = '#fff';
          wrapper.style.padding = '16px';
          wrapper.style.borderRadius = '8px';
          wrapper.style.boxShadow = '0 8px 24px rgba(0,0,0,0.2)';
          wrapper.style.position = 'relative';
          wrapper.style.maxHeight = 'calc(100vh - 24px)';
          wrapper.style.overflowY = 'auto';
          wrapper.style.margin = '12px auto';
          wrapper.style.paddingBottom = '24px';
          try {
            wrapper.style.setProperty('-webkit-overflow-scrolling', 'touch');
          } catch (e) {}

          const container = document.createElement('div');
          container.id = 'card-element-embedded';
          container.style.width = '100%';
          container.style.minHeight = '64px';
          container.style.boxSizing = 'border-box';

          const closeBtn = document.createElement('button');
          closeBtn.setAttribute('aria-label', 'Close checkout');
          closeBtn.innerText = '\u00d7';
          closeBtn.style.position = 'absolute';
          closeBtn.style.top = '8px';
          closeBtn.style.right = '8px';
          closeBtn.style.background = 'transparent';
          closeBtn.style.border = 'none';
          closeBtn.style.fontSize = '24px';
          closeBtn.style.cursor = 'pointer';
          closeBtn.style.lineHeight = '1';
          closeBtn.style.padding = '4px';
          closeBtn.style.zIndex = '100001';

          wrapper.appendChild(container);
          wrapper.appendChild(closeBtn);
          overlay.appendChild(wrapper);
          document.body.appendChild(overlay);
          this.createdMountElement = overlay;

          const overlayClickHandler = (evt: Event) => {
            if (evt.target === overlay) {
              try {
                if (this.onExitCallback) this.onExitCallback();
              } catch (e) {}
              try {
                if (
                  this.createdMountElement &&
                  this.createdMountElement.parentNode
                ) {
                  this.createdMountElement.parentNode.removeChild(
                    this.createdMountElement
                  );
                }
              } catch (e) {}
              this.createdMountElement = undefined;
            }
          };
          overlay.addEventListener('click', overlayClickHandler);
          this.overlayClickHandler = overlayClickHandler;

          const keyHandler = (evt: KeyboardEvent) => {
            if (evt.key === 'Escape' || evt.key === 'Esc') {
              try {
                if (this.onExitCallback) this.onExitCallback();
              } catch (e) {}
              try {
                if (
                  this.createdMountElement &&
                  this.createdMountElement.parentNode
                ) {
                  this.createdMountElement.parentNode.removeChild(
                    this.createdMountElement
                  );
                }
              } catch (e) {}
              this.createdMountElement = undefined;
            }
          };
          document.addEventListener('keydown', keyHandler);
          this.overlayKeyHandler = keyHandler;

          mountTarget = container;
        } catch (e) {
        }
      }

      try {
        // mount accepts either a selector string or an Element reference
        if (mountTarget) {
          checkoutForm.mount(mountTarget as any);
        } else {
          // fallback to selector string; this will throw the original IntegrationError
          checkoutForm.mount('#card-element');
        }
      } catch (e) {
        // If mounting fails, ensure exit callback is invoked so caller can cleanup
        try {
          onExit();
        } catch (err) {}
        return;
      }
      this.checkoutForm = checkoutForm;
    } catch (err) {
      try {
        onExit();
      } catch (e) {}
      return;
    }

    ///

    //
    //
    //var card = webStripe?.elements().create('card', { style: style })!;
    //// Stripe injects an iframe into the DOM
    //// Stripe injects an iframe into the DOM
    //card.mount('#card-element');
    //
    //card.on('loaderror', function (event) {
    //  // Listen for load errors, handling any errors using your own observability tooling.
    //});
    //
    //card.on('change', function (event) {
    //  // Disable the Pay button if there are no card details in the Element
    //  document.querySelector('button')!.disabled = event.empty;
    //  document.querySelector('#card-error')!.textContent = event.error
    //    ? event.error.message
    //    : '';
    //});
    //
    //var form = document.getElementById('payment-form')!;
    //form.addEventListener('submit', function (event) {
    //  event.preventDefault();
    //  // Show a loading state on the submit button
    //  var button = document.querySelector('#submit')! as HTMLButtonElement;
    //
    //  button.disabled = true;
    //
    //  // Clear previous error messages
    //  document.querySelector('#card-error')!.textContent = '';
    //
    //  // Complete payment when the submit button is clicked
    //  payWithCard(webStripe, card, session.clientSecret);
    //});
    //
    //var payWithCard = function (stripe: any, card: any, clientSecret: any) {
    //  let parsedClientSecret = clientSecret;
    //
    //  console.log('Using client secret format:', parsedClientSecret);
    //
    //  stripe
    //    .confirmCardPayment(parsedClientSecret, {
    //      payment_method: {
    //        card: card,
    //      },
    //    })
    //    .then(function (result: any) {
    //      if (result.error) {
    //        console.log('Payment error:', result.error.message);
    //        document.querySelector('#card-error')!.textContent =
    //          result.error.message;
    //        onExit();
    //      } else {
    //        console.log('Payment succeeded!: ', result);
    //        onSuccess();
    //      }
    //    });
    //};
    //return;
  }

  /**
   * Crear un método de pago
   * @param paymentMethodData Los datos del método de pago
   */
  public async createPaymentMethod(paymentMethodData: any) {
    // await this.initializeStripe();
    // // Lógica para crear método de pago
    // return Stripe.createPaymentMethod(paymentMethodData);
  }

  // Otros métodos relacionados con pagos...
}
